import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    Box,
    Card,
    CardContent,
    Grid,
    Typography,
    Chip,
    Alert,
    Skeleton,
} from '@mui/material'
import {
    CheckCircle as HealthyIcon,
    Error as ErrorIcon,
} from '@mui/icons-material'
import { request } from '../common/services/request'
import { API_ENDPOINTS } from '../common/endpoints'
import { getPatientDetailRoute } from '../common/constant'
import Schedules from './Schedules'
import type {
    HealthResponse,
    ConfigResponse,
    OpenEMRStatusResponse,
    Patient,
    GenerationJob,
} from '../common/types'

function Dashboard() {
    const navigate = useNavigate()
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [health, setHealth] = useState<HealthResponse | null>(null)
    const [config, setConfig] = useState<ConfigResponse | null>(null)
    const [openemrStatus, setOpenemrStatus] = useState<OpenEMRStatusResponse | null>(null)
    const [patientCount, setPatientCount] = useState(0)
    const [recentJobs, setRecentJobs] = useState<GenerationJob[]>([])
    const [recentPatients, setRecentPatients] = useState<Patient[]>([])

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true)
                const client = request()

                const [healthRes, configRes] = await Promise.all([
                    client.get<HealthResponse>(API_ENDPOINTS.HEALTH),
                    client.get<ConfigResponse>(API_ENDPOINTS.CONFIG),
                ])
                setHealth(healthRes.data)
                setConfig(configRes.data)
                try {
                    const dashboardHealthRes = await client.get<HealthResponse>(API_ENDPOINTS.HEALTH)
                    setOpenemrStatus({ status: dashboardHealthRes.data.status, base_url: '', authenticated: true })
                } catch {
                    setOpenemrStatus({ status: 'error', base_url: '', authenticated: false })
                }

                // Fetch patient count and recent patients
                const patientsRes = await client.get(API_ENDPOINTS.PATIENTS.BASE, {
                    params: { page: 1, page_size: 5 },
                })
                setPatientCount(patientsRes.data.total)
                setRecentPatients(patientsRes.data.items)

                // Fetch recent jobs
                const jobsRes = await client.get(API_ENDPOINTS.JOBS.BASE, {
                    params: { page: 1, page_size: 5 },
                })
                setRecentJobs(jobsRes.data.items)
            } catch (e: any) {
                setError(e.message || 'Failed to load dashboard data')
            } finally {
                setLoading(false)
            }
        }

        fetchData()
    }, [])

    return (
        <Box>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                    <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
                        OpenEMR Controller
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        System overview and quick stats
                    </Typography>
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {/* Status Cards */}
            <Grid container spacing={2} mb={3} alignItems="stretch">
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f5f5f5' }, display: 'flex', flexDirection: 'column', height: '100%' }}>
                        <CardContent sx={{ p: '20px', flexGrow: 1 }}>
                            <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#333333' }}>
                                API {`v${health?.version || config?.version}`}
                            </Typography>
                            <Box sx={{ mt: 1 }}>
                                {loading ? (
                                    <Skeleton variant="rounded" width={92} height={24} />
                                ) : (
                                    <Chip
                                        icon={health?.status === 'healthy' ? <HealthyIcon /> : <ErrorIcon />}
                                        label={health?.status || 'Unknown'}
                                        color={health?.status === 'healthy' ? 'success' : 'error'}
                                        size="small"
                                    />
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f5f5f5' }, display: 'flex', flexDirection: 'column', height: '100%' }}>
                        <CardContent sx={{ p: '20px', flexGrow: 1 }}>
                            <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#333333' }}>
                                OpenEMR Server
                            </Typography>
                            <Box sx={{ mt: 1 }}>
                                {loading ? (
                                    <Skeleton variant="rounded" width={92} height={24} />
                                ) : (
                                    <Chip
                                        icon={openemrStatus?.status === 'healthy' ? <HealthyIcon /> : <ErrorIcon />}
                                        label={openemrStatus?.status === 'healthy' ? 'Healthy' : 'Unhealthy'}
                                        color={openemrStatus?.status === 'healthy' ? 'success' : 'error'}
                                        size="small"
                                    />
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f5f5f5' }, display: 'flex', flexDirection: 'column', height: '100%' }} onClick={() => navigate('/patients')}>
                        <CardContent sx={{ p: '20px', flexGrow: 1 }}>
                            <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#333333' }}>
                                Patients
                            </Typography>
                            <Box sx={{ mt: 1 }}>
                                {loading ? (
                                    <Skeleton variant="text" width={60} height={40} />
                                ) : (
                                    <Typography sx={{ fontSize: '28px', fontWeight: 700, color: '#000000' }}>
                                        {patientCount}
                                    </Typography>
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f5f5f5' }, display: 'flex', flexDirection: 'column', height: '100%' }} onClick={() => navigate('/jobs')}>
                        <CardContent sx={{ p: '20px', flexGrow: 1 }}>
                            <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#333333' }}>
                                Total Jobs
                            </Typography>
                            <Box sx={{ mt: 1 }}>
                                {loading ? (
                                    <Skeleton variant="text" width={60} height={40} />
                                ) : (
                                    <Typography sx={{ fontSize: '28px', fontWeight: 700, color: '#000000' }}>
                                        {recentJobs?.length ?? 0}
                                    </Typography>
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Schedules (embedded) */}
            <Grid container spacing={2} alignItems="stretch" sx={{ mb: 2 }}>
                <Grid item xs={12}>
                    <Card sx={{ display: 'flex', flexDirection: 'column' }}>
                        <CardContent sx={{ p: '12px' }}>
                            <Typography variant="h6" fontWeight={600} gutterBottom>
                                Schedules
                            </Typography>
                            <Box sx={{ maxHeight: 500, overflow: 'auto' }}>
                                <Schedules compact />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Recent Patients */}
            <Grid container spacing={2} alignItems="stretch">
                <Grid item xs={12} md={6}>
                    <Card sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                        <CardContent sx={{ p: '20px', flexGrow: 1 }}>
                            <Typography variant="h6" fontWeight={600} gutterBottom>
                                Recent Patients
                            </Typography>
                            {loading ? (
                                <Box>
                                    {[1, 2, 3].map((i) => (
                                        <Box key={i} sx={{ py: 1.5, px: 2, borderBottom: '1px solid #eee' }}>
                                            <Skeleton variant="text" width="70%" />
                                            <Skeleton variant="text" width="40%" />
                                        </Box>
                                    ))}
                                </Box>
                            ) : !recentPatients || recentPatients.length === 0 ? (
                                <Typography color="textSecondary">No patients yet</Typography>
                            ) : (
                                recentPatients.map((patient) => (
                                    <Box
                                        key={patient.uuid}
                                        sx={{
                                            py: 1,
                                            px: 2,
                                            borderBottom: '1px solid #eee',
                                            cursor: 'pointer',
                                            '&:hover': { bgcolor: 'action.hover' },
                                        }}
                                        onClick={() => navigate(getPatientDetailRoute(patient.uuid))}
                                    >
                                        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                                            <Typography fontWeight={500} sx={{ flex: 1, minWidth: 0, fontSize: '0.95rem' }}>
                                                {patient.fname} {patient.lname}
                                            </Typography>
                                            <Box display="flex" gap={1} alignItems="center">
                                                <Chip
                                                    label={patient.sex || 'Unknown'}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                                {patient.DOB && (
                                                    <Chip label={`DOB: ${patient.DOB}`} size="small" variant="outlined" />
                                                )}
                                            </Box>
                                        </Box>
                                    </Box>
                                ))
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Card sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                        <CardContent sx={{ p: '20px', flexGrow: 1 }}>
                            <Typography variant="h6" fontWeight={600} gutterBottom>
                                Recent Jobs
                            </Typography>
                            {loading ? (
                                <Box>
                                    {[1, 2, 3].map((i) => (
                                        <Box key={i} sx={{ py: 1.5, borderBottom: '1px solid #eee' }}>
                                            <Skeleton variant="text" width="55%" />
                                            <Skeleton variant="text" width="45%" />
                                        </Box>
                                    ))}
                                </Box>
                            ) : !recentJobs || recentJobs.length === 0 ? (
                                <Typography color="textSecondary">No jobs yet</Typography>
                            ) : (
                                recentJobs.map((job) => (
                                    <Box
                                        key={job.id}
                                        sx={{
                                            py: 1,
                                            borderBottom: '1px solid #eee',
                                        }}
                                    >
                                        <Box display="flex" justifyContent="space-between" alignItems="center">
                                            <Typography>Job #{job.id}</Typography>
                                            <Chip
                                                label={job.status}
                                                size="small"
                                                color={
                                                    job.status === 'completed'
                                                        ? 'success'
                                                        : job.status === 'failed'
                                                            ? 'error'
                                                            : job.status === 'running'
                                                                ? 'info'
                                                                : 'warning'
                                                }
                                            />
                                        </Box>
                                        <Typography variant="caption" color="textSecondary">
                                            Generated: {job.vitals_generated} | Pushed: {job.vitals_pushed}
                                        </Typography>
                                    </Box>
                                ))
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    )
}

export default Dashboard
